import { PartialDeep } from "type-fest";

import { Price, Product, ProductCondition } from "@/Apollo/schema";
import { LISTING_CURRENCY } from "@/common/static/currency.static";

type ProductPriceInfo = {
  price: Price;
  soldAtPrice?: Price | null;
  originalPrice: Price;
  bidPrice?: Price | null;
  discounted: boolean;
  discountAmount: number;
  discountPercentage: number;
};

/**
 * Gets product price info, such as current price, sold at price, original price, bid price, whether the product is discounted, discount amount and percentage.
 *
 * @param product Product to get the price info from.
 */
export const getPriceInfo = (
  product?: PartialDeep<Product, { recurseIntoArrays: true }> | null
): ProductPriceInfo => {
  const buyNowPrice = product?.auction?.buy_now_price;
  const buyNowPriceAmount = buyNowPrice?.amount ?? 0;

  const soldAt = product?.auction?.sold_at;

  const soldAtPrice: Price | undefined = soldAt
    ? {
        __typename: "Price",
        amount: soldAt?.amount ?? 0,
        currency: soldAt?.currency ?? LISTING_CURRENCY,
      }
    : undefined;

  const productBidPrice = product?.auction?.minimum_bid;

  const bidPrice: Price | undefined = productBidPrice
    ? {
        __typename: "Price",
        amount: productBidPrice?.amount ?? 0,
        currency: productBidPrice?.currency ?? LISTING_CURRENCY,
      }
    : undefined;

  const originalPrice = product?.original_price;
  const originalPriceAmount = originalPrice?.amount ?? buyNowPriceAmount;

  const prevPrice =
    buyNowPriceAmount < originalPriceAmount ? originalPrice : undefined;

  const prevPriceAmount = prevPrice?.amount ?? 0;

  const discounted = buyNowPriceAmount < prevPriceAmount;
  const discountAmount = discounted ? prevPriceAmount - buyNowPriceAmount : 0;
  const discountPercentage = discounted
    ? Math.round((discountAmount / prevPriceAmount) * 100)
    : 0;

  return {
    price: {
      __typename: "Price",
      amount: buyNowPriceAmount,
      currency: buyNowPrice?.currency ?? LISTING_CURRENCY,
    },
    soldAtPrice,
    originalPrice: {
      __typename: "Price",
      amount: originalPriceAmount,
      currency: originalPrice?.currency ?? LISTING_CURRENCY,
    },
    bidPrice,
    discounted,
    discountAmount,
    discountPercentage,
  };
};

export const getIsOfCondition = (
  product: PartialDeep<Product, { recurseIntoArrays: true }>,
  condition: ProductCondition
) => {
  return product.condition === condition;
};

/**
 * Converts a given amount into a Price object.
 * If the amount is null or undefined, it returns undefined.
 *
 * @param [amount] The numerical value to be converted into a Price object.
 *
 * @returns A Price object containing the amount and a default currency (EUR), or undefined if no valid amount is provided.
 */
export const getPriceObjectFromAmount = (
  amount?: number | null
): Price | undefined => {
  if (!amount) {
    return;
  }

  return {
    amount,
    currency: LISTING_CURRENCY,
  };
};

/**
 * Whether a product qualifies for the "Curated" label.
 */
export const isProductCurated = (
  product?: PartialDeep<Product, { recurseIntoArrays: true }> | null
): boolean => {
  return product?.isCurated ?? false;
};

/**
 * Whether the product is a bag.
 *
 * Detected by the presence of any bag-only attribute (`gender`,
 * `bag_model`, `hardware_color`). Language-agnostic — does not depend
 * on category slug.
 */
export const isBagsProduct = (
  product?: PartialDeep<Product, { recurseIntoArrays: true }> | null
): boolean => {
  if (!product) {
    return false;
  }

  return !!(product.gender || product.bag_model || product.hardware_color);
};
